import type { CustomerZoneGetDefaultResponseType } from "@sellar-io/api-v2/types";

export type ConnectionDeliveryState = {
  fulfillmentType?:
    | "direct"
    | "courier"
    | "haulier"
    | "sellarDistribution"
    | "collection";
  drayDeliveryMethodVisible: boolean;
  courierDeliveryMethodVisible: boolean;
  palletDeliveryMethodVisible: boolean;
};

type DeliveryZoneMethods = Pick<
  CustomerZoneGetDefaultResponseType,
  "DrayDeliveryMethodId" | "CourierDeliveryMethodId" | "PalletDeliveryMethodId"
>;

export const getConnectionDeliveryAvailability = ({
  zone,
  hasSDArea,
  allowsCollection,
}: {
  zone: DeliveryZoneMethods | undefined;
  hasSDArea: boolean;
  allowsCollection: boolean;
}) =>
  zone
    ? {
        direct: !!zone.DrayDeliveryMethodId,
        courier: !!zone.CourierDeliveryMethodId,
        haulier: !!zone.PalletDeliveryMethodId,
        sellarDistribution: hasSDArea,
        collection: allowsCollection,
      }
    : undefined;

export type ConnectionDeliveryAvailability = NonNullable<
  ReturnType<typeof getConnectionDeliveryAvailability>
>;

export const resolveConnectionDeliveryState = ({
  state,
  availability,
  isDirectZone,
}: {
  state: ConnectionDeliveryState;
  availability: ConnectionDeliveryAvailability | undefined;
  isDirectZone: boolean;
}): ConnectionDeliveryState => {
  // An unresolved zone is not evidence that the selected method is unavailable.
  if (!availability) return state;

  const next = { ...state };
  // Preserve the courier-only restriction introduced by ENG-4973. Selecting
  // Direct explicitly changes the default and therefore releases this rule.
  if (isDirectZone && next.fulfillmentType === "courier") {
    next.drayDeliveryMethodVisible = false;
  }

  const visibleMethods = {
    sellarDistribution: availability.sellarDistribution,
    direct: availability.direct && next.drayDeliveryMethodVisible,
    courier: availability.courier && next.courierDeliveryMethodVisible,
    haulier: availability.haulier && next.palletDeliveryMethodVisible,
    collection: availability.collection,
  };

  if (
    next.fulfillmentType &&
    ["direct", "courier", "haulier"].includes(next.fulfillmentType) &&
    !visibleMethods[next.fulfillmentType]
  ) {
    const priority = [
      "sellarDistribution",
      "direct",
      "courier",
      "haulier",
      "collection",
    ] as const;
    next.fulfillmentType =
      priority.find((method) => visibleMethods[method]) ?? next.fulfillmentType;
  }

  if (isDirectZone && next.fulfillmentType === "courier") {
    next.drayDeliveryMethodVisible = false;
  }
  return next;
};
